import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../TopBar';
import Navbar from '../Navbar';
import Footer from '../Footer';
import FloatingButtons from '../FloatingButtons';
import SEO from '../SEO';

const getElapsedMonths = (startDate) => {
  const now = new Date();
  let months =
    (now.getFullYear() - startDate.getFullYear()) * 12 +
    (now.getMonth() - startDate.getMonth());

  if (now.getDate() < startDate.getDate()) {
    months -= 1;
  }

  return Math.max(months, 0);
};

const useInView = (ref, threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold });

    if (ref?.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isVisible;
};

export default function AboutPage() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeFilter, setActiveFilter] = useState('All');
  const statsRef = useRef(null);
  const valuesRef = useRef(null);
  const servicesRef = useRef(null);
  const projectsRef = useRef(null);
  const leadershipRef = useRef(null);
  const statsVisible = useInView(statsRef, 0.2);
  const valuesVisible = useInView(valuesRef, 0.2);
  const servicesVisible = useInView(servicesRef, 0.2);
  const projectsVisible = useInView(projectsRef, 0.2);
  const leadershipVisible = useInView(leadershipRef, 0.2);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;
      setScrollProgress(progress);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const teamExperienceMonths = getElapsedMonths(new Date(2025, 9, 1));

  const stats = [
    { label: 'Projects Delivered', value: '15+' },
    { label: 'Active Clients', value: '20+' },
    { label: 'Expert Team Members', value: '12+' },
    { label: 'Core Team Experience', value: `${teamExperienceMonths}+ Months` },
  ];

  const values = [
    {
      title: 'Security-First Engineering',
      desc: 'Every solution starts with risk-aware architecture, role-based access, and operational safeguards.',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />,
    },
    {
      title: 'Business-Ready Delivery',
      desc: 'We prioritize practical outcomes, measurable goals, and solutions that align with operations.',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
    {
      title: 'Transparent Execution',
      desc: 'Milestone-based plans, clear progress updates, and process-led delivery at every stage.',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4V9m3 8H6a2 2 0 01-2-2V7a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2z" />,
    },
    {
      title: 'Long-Term Partnership',
      desc: 'We work as an extension of your team with dedicated support and continuous improvements.',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
    },
  ];

  const credentials = [
    'Registered as a private limited company in February 2026.',
    `Core delivery team active for ${teamExperienceMonths}+ months.`,
    'Security-first delivery standards across cloud and applications.',
    'Transparent, process-driven execution with milestone-based updates.',
  ];

  const services = [
    'Cloud Infrastructure',
    'Cybersecurity Solutions',
    'Enterprise Software',
    'Digital Transformation',
    'Business Hours Support (10 AM to 5 PM IST)',
  ];

  const industries = [
    'Education & EdTech',
    'Healthcare & Clinics',
    'Retail & E-commerce',
    'Manufacturing & Distribution',
    'Professional Services',
    'Real Estate & Construction',
    'Public Services & NGOs',
  ];

  const caseHighlights = [
    {
      title: 'CA Successful - Online Learning',
      category: 'Education',
      desc: 'Comprehensive e-learning platform for CA education with live classes, study materials, and progress tracking.',
      link: 'https://www.casuccessful.com/',
      stats: { year: '2026', type: 'Full Stack' },
    },
    {
      title: 'Apna Safar Bhopal',
      category: 'Transport',
      desc: 'Taxi booking and management platform with real-time tracking, driver management, and payment integration.',
      link: 'https://apnasafarbhopal.in/',
      stats: { year: '2026', type: 'Full Stack' },
    },
    {
      title: 'Madhav Farmhouse Sironj',
      category: 'Farmhouse',
      desc: 'Complete hospitality website with booking system, gallery, services showcase, and customer engagement.',
      link: 'https://madhavfarmhousesironj.com/',
      stats: { year: '2025', type: 'Full Stack' },
    },
    {
      title: 'Madhav Restaurant Portal',
      category: 'Restaurant',
      desc: 'Specialized restaurant landing with menu display, online reservations, and order management features.',
      link: 'https://madhavrestaurant.madhavfarmhousesironj.com/',
      stats: { year: '2025', type: 'Frontend' },
    },
    {
      title: 'Rajesh Patel - Professional Portfolio',
      category: 'Professional Services',
      desc: 'Executive portfolio website showcasing professional achievements, services, and client testimonials.',
      link: 'https://rajeshpatelsironj.com/',
      stats: { year: '2025', type: 'Frontend' },
    },
    {
      title: 'Neetu Thakur - Professional Portfolio',
      category: 'Professional Services',
      desc: 'Modern portfolio showcasing professional expertise, services, and career achievements with contact integration.',
      link: 'https://neetuthakur.in/',
      stats: { year: '2026', type: 'Frontend' },
    },
    {
      title: 'Rajeev Jain - Business Portal',
      category: 'Professional Services',
      desc: 'Professional business website with service listings, team information, and lead generation features.',
      link: 'https://rajeevjainsironj.in/',
      stats: { year: '2026', type: 'Full Stack' },
    },
    {
      title: 'Aahan - Professional Website',
      category: 'Professional Services',
      desc: 'Professional services website with appointment scheduling, service showcase, and client management.',
      link: 'https://aahan.rajeevjainsironj.in/',
      stats: { year: '2026', type: 'Frontend' },
    },
    {
      title: 'Neelam Singh Yadav - Portfolio',
      category: 'Professional Services',
      desc: 'Professional portfolio website with expertise showcase, services list, and client contact features.',
      link: 'https://neelamsinghyadav.in/',
      stats: { year: '2025', type: 'Frontend' },
    },
    {
      title: 'Rahul Tyagi - Professional Presence',
      category: 'Professional Services',
      desc: 'Executive portfolio website with professional achievements, service offerings, and business contact details.',
      link: 'https://rahultyagi.madhavfarmhousesironj.com/',
      stats: { year: '2025', type: 'Frontend' },
    },
  ];

  const categoryColors = {
    'Education': { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', icon: '🎓' },
    'Transport': { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', icon: '🚗' },
    'Professional Services': { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', icon: '💼' },
    'Hospitality': { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', icon: '🏨' },
    'Restaurant': { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', icon: '🍽️' },
    'Healthcare': { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', icon: '⚕️' },
    'Web Design': { bg: 'bg-pink-50', border: 'border-pink-200', badge: 'bg-pink-100 text-pink-700', icon: '🎨' },
  };

  const allCategories = ['All', ...new Set(caseHighlights.map(p => p.category).filter(cat => !['Healthcare', 'Hospitality'].includes(cat)))];
  const filteredProjects = activeFilter === 'All' 
    ? caseHighlights 
    : caseHighlights.filter(p => p.category === activeFilter);

  const leadership = [
    {
      name: 'Sachin Kurmi',
      role: 'Director - Strategy & Operations',
      image: '/img/sachin.jpeg',
      bio: 'Leads business strategy, partnerships, and operations to ensure consistent client outcomes and sustainable growth.',
      social: {
        linkedin: 'https://www.linkedin.com',
        twitter: 'https://twitter.com',
        email: 'mailto:sachin@garudstacks.com',
      },
    },
    {
      name: 'Dharmendra Singh',
      role: 'Director - Technology & Delivery',
      image: '/img/dharmendra1.jpeg',
      bio: 'Leads technical architecture, engineering delivery, and quality governance across all production projects.',
      social: {
        linkedin: 'https://www.linkedin.com',
        twitter: 'https://twitter.com',
        email: 'mailto:dharmendra@garudstacks.com',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-amber-200 selection:text-amber-900">
      <SEO 
        title="About Garud Stacks - Enterprise Technology Solutions"
        description="Learn about Garud Stacks, a security-first IT services company delivering enterprise technology solutions, cloud computing, AI, and digital transformation services."
        image="https://garudstacks.tech/img/about-og.png"
        url="https://garudstacks.tech/about"
        keywords="about garud stacks, enterprise technology, IT services, digital transformation, security-first engineering"
      />
      <TopBar />
      <Navbar />

      <div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500 z-[60]"
        style={{ transform: `scaleX(${scrollProgress})`, transformOrigin: 'left' }}
      />

      <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-24 overflow-hidden border-b border-slate-200">
        <div className="absolute top-10 -left-28 w-80 h-80 bg-amber-300/20 rounded-full blur-3xl pointer-events-none animate-pulse" />
        <div className="absolute -bottom-14 right-0 w-96 h-96 bg-orange-300/20 rounded-full blur-3xl pointer-events-none animate-pulse" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-amber-200/10 to-orange-200/10 rounded-full blur-3xl pointer-events-none animate-spin-slow" />

        <div className="relative max-w-5xl mx-auto px-6 text-center animate-fadeInUp">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-white to-amber-50 border border-amber-200 shadow-lg shadow-amber-300/20 mb-7 hover:shadow-xl hover:border-amber-400 transition-all duration-300 group">
            <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 animate-pulse" />
            <span className="text-xs font-bold text-slate-700 uppercase tracking-widest">
              🚀 Security-First IT Services Company
            </span>
            <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-orange-600 to-amber-500 animate-pulse" />
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.08] mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 animate-gradient-shift">Garud Stacks</span>
          </h1>

          <div className="h-1 w-24 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 mx-auto mb-8 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />

          <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed font-medium mb-10 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            Garud Stacks helps organizations build <span className="font-bold text-amber-600">reliable software</span>, <span className="font-bold text-orange-600">secure infrastructure</span>, and <span className="font-bold text-red-600">scalable digital systems</span>.
            <br />
            <span className="text-slate-500">We are a registered private limited company focused on practical delivery, transparent execution, and measurable outcomes.</span>
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            <Link
              to="/contact"
              className="px-8 py-3.5 w-full sm:w-auto bg-gradient-to-r from-slate-900 to-slate-800 text-white font-bold rounded-xl hover:from-slate-800 hover:to-slate-700 transition-all hover:shadow-2xl hover:-translate-y-1 text-sm tracking-wide hover:shadow-slate-900/40 border border-slate-700 group overflow-hidden relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-500 opacity-0 group-hover:opacity-10 transition-opacity" />
              <span className="relative flex items-center gap-2">Talk to an Expert <span className="group-hover:translate-x-1 transition-transform">→</span></span>
            </Link>
            <a
              href="/"
              className="px-8 py-3.5 w-full sm:w-auto bg-white text-slate-900 font-bold rounded-xl border-2 border-slate-200 hover:border-amber-300 hover:bg-amber-50 transition-all text-sm tracking-wide hover:-translate-y-1 group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 to-orange-500/0 group-hover:from-amber-500/10 group-hover:to-orange-500/10 transition-all" />
              <span className="relative">Explore Solutions</span>
            </a>
          </div>
        </div>
      </section>

      <section ref={statsRef} className="py-12 lg:py-16 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-300/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-300/10 rounded-full blur-3xl animate-pulse" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 relative z-10">
          {stats.map((stat, idx) => (
            <div 
              key={stat.label} 
              className="bg-white/80 backdrop-blur-sm border border-slate-200/50 p-6 rounded-2xl text-center shadow-sm hover:shadow-2xl hover:border-amber-300 hover:bg-white transition-all duration-500 hover:-translate-y-2 group"
              style={{
                animation: statsVisible ? `fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards` : 'none',
                animationDelay: statsVisible ? `${idx * 0.1}s` : '0s',
                opacity: statsVisible ? 1 : 0,
              }}
            >
              <div className="relative mb-3">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-300/0 to-orange-300/0 group-hover:from-amber-300/10 group-hover:to-orange-300/10 rounded-xl blur-xl transition-all duration-300" />
                <div className="relative text-2xl sm:text-3xl lg:text-4xl font-black bg-gradient-to-r from-slate-900 to-slate-700 group-hover:from-amber-600 group-hover:to-orange-600 bg-clip-text text-transparent transition-all duration-300">{stat.value}</div>
              </div>
              <div className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest group-hover:text-amber-600 transition-colors">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200 group animate-fadeInLeft">
            <img 
              src="/img/about.png" 
              className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-500" 
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/25 to-transparent group-hover:from-slate-900/40 transition-all duration-300" />
          </div>

          <div className="animate-fadeInRight">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6 tracking-tight">
              Building Practical Digital Systems For Growing Businesses
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-5">
              We started with one clear objective: deliver dependable technology that supports real business operations.
              Our focus is on architecture quality, security controls, and predictable execution.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              From cloud and cybersecurity to custom software and automation, our team works hands-on with clients
              to deliver systems that are maintainable, scalable, and aligned with measurable goals.
            </p>

            <div className="bg-gradient-to-br from-slate-50 to-amber-50/30 border border-slate-200 rounded-2xl p-6 hover:border-amber-300 hover:shadow-lg transition-all duration-300">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest mb-4">Company Credentials</h3>
              <ul className="space-y-3">
                {credentials.map((item, idx) => (
                  <li key={item} className="flex items-start gap-3 text-slate-600 hover:text-slate-900 transition-colors" style={{ animationDelay: `${idx * 0.05}s` }}>
                    <span className="text-amber-500 font-bold mt-0.5 text-lg">✓</span>
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={valuesRef}
        className="py-16 lg:py-24 bg-gradient-to-b from-white via-slate-50 to-white border-b border-slate-200 relative overflow-hidden"
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-0 w-96 h-96 bg-purple-200/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-200/10 rounded-full blur-3xl animate-pulse" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div 
              style={{
                animation: valuesVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
                opacity: valuesVisible ? 1 : 0,
              }}
              className="mb-4"
            >
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-xs font-bold text-amber-700 uppercase tracking-widest mb-4 shadow-sm">
                <span className="text-lg">⭐</span> Core Values
              </span>
            </div>
            <h2 
              className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-5 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 via-slate-900 to-slate-600"
              style={{
                animation: valuesVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
                animationDelay: '0.1s',
                opacity: valuesVisible ? 1 : 0,
              }}
            >
              What Defines Our Delivery
            </h2>
            <p 
              className="text-lg text-slate-600 leading-relaxed"
              style={{
                animation: valuesVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
                animationDelay: '0.2s',
                opacity: valuesVisible ? 1 : 0,
              }}
            >
              We combine structured process, clear communication, and engineering discipline to reduce execution risk.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {values.map((val, idx) => (
              <div 
                key={val.title} 
                className="relative overflow-hidden"
                style={{
                  animation: valuesVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
                  animationDelay: valuesVisible ? `${idx * 0.12}s` : '0s',
                  opacity: valuesVisible ? 1 : 0,
                }}
              >
                <div className="bg-white/70 backdrop-blur-md rounded-3xl p-8 border border-slate-200/60 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group h-full relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-amber-500/0 before:via-transparent before:to-orange-500/0 before:opacity-0 before:group-hover:opacity-5 before:transition-all before:duration-300">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 via-transparent to-orange-500/0 group-hover:from-amber-500/5 group-hover:to-orange-500/5 transition-all duration-300 pointer-events-none" />
                  
                  <div className="relative">
                    <div className="w-16 h-16 mb-6 relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-300 to-orange-300 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-300 group-hover:scale-125" />
                      <div className="relative w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-amber-300 text-amber-600 rounded-2xl flex items-center justify-center group-hover:shadow-lg group-hover:scale-110 group-hover:border-amber-500 transition-all duration-300">
                        <svg className="w-7 h-7 group-hover:rotate-6 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          {val.icon}
                        </svg>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-amber-600 transition-colors">{val.title}</h3>
                    <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors font-medium">{val.desc}</p>
                  </div>

                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-amber-200/20 to-orange-200/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section ref={servicesRef} className="py-16 lg:py-24 bg-gradient-to-b from-white to-slate-50 border-b border-slate-200 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-green-200/10 rounded-full blur-3xl pointer-events-none animate-pulse" />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-blue-200/10 rounded-full blur-3xl pointer-events-none animate-pulse" />
        
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 lg:gap-12 relative z-10">
          <div 
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 lg:p-10 text-white border border-slate-700/50 shadow-xl hover:shadow-2xl transition-all duration-500 group relative overflow-hidden"
            style={{
              animation: servicesVisible ? 'fadeInLeft 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
              opacity: servicesVisible ? 1 : 0,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-orange-500/0 group-hover:from-amber-500/10 group-hover:to-orange-500/10 transition-all duration-300" />
            <div className="absolute -top-20 -right-20 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300 text-xs font-bold uppercase tracking-widest mb-5 border border-amber-500/40 group-hover:border-amber-400 group-hover:bg-amber-500/30 transition-all">
                <span>🎯</span>
                <span>What We Offer</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold mb-8 text-white group-hover:text-amber-300 transition-colors">Core Services</h3>
              <ul className="space-y-4">
                {services.map((service, idx) => (
                  <li 
                    key={service} 
                    className="flex items-start gap-3 text-slate-200 group/item hover:text-amber-300 transition-colors cursor-pointer"
                    style={{
                      animation: servicesVisible ? 'fadeInLeft 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
                      animationDelay: servicesVisible ? `${0.1 + idx * 0.08}s` : '0s',
                      opacity: servicesVisible ? 1 : 0,
                    }}
                  >
                    <span className="text-amber-400 font-bold mt-1 text-lg group-hover/item:translate-x-2 group-hover/item:scale-125 transition-all">✦</span>
                    <span className="font-medium leading-relaxed group-hover/item:translate-x-1 transition-transform">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-amber-500/5 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500" />
          </div>

          <div 
            className="bg-gradient-to-br from-slate-50 via-white to-amber-50 rounded-3xl p-8 lg:p-10 border border-slate-200/60 shadow-sm hover:shadow-2xl transition-all duration-500 group relative overflow-hidden"
            style={{
              animation: servicesVisible ? 'fadeInRight 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
              opacity: servicesVisible ? 1 : 0,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-purple-500/0 group-hover:from-blue-500/5 group-hover:to-purple-500/5 transition-all duration-300" />
            <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 text-xs font-bold uppercase tracking-widest mb-5 border border-blue-200 group-hover:border-blue-400 group-hover:bg-blue-100 transition-all">
                <span>🌍</span>
                <span>Industries</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-8">Industries We Support</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {industries.map((industry, idx) => (
                  <div 
                    key={industry} 
                    className="px-5 py-3.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-medium hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 transition-all duration-300 group/item"
                    style={{
                      animation: servicesVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
                      animationDelay: servicesVisible ? `${0.1 + idx * 0.06}s` : '0s',
                      opacity: servicesVisible ? 1 : 0,
                    }}
                  >
                    <span className="flex items-center gap-2 group-hover/item:translate-x-1 transition-transform">
                      <span>🏢</span>
                      <span>{industry}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-500/5 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500" />
          </div>
        </div>
      </section>

      <section ref={projectsRef} id="selected-work-highlights" className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 border-b border-slate-200 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-200/10 rounded-full blur-3xl pointer-events-none animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-200/10 rounded-full blur-3xl pointer-events-none animate-pulse" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div 
            className="text-center max-w-3xl mx-auto mb-14"
            style={{
              animation: projectsVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
              opacity: 1,
            }}
          >
            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 mb-6 hover:shadow-md hover:border-purple-400 transition-all group shadow-sm">
              <span className="text-2xl group-hover:animate-spin">✨</span>
              <span className="text-xs font-bold text-purple-700 uppercase tracking-widest">Portfolio</span>
              <span className="text-2xl group-hover:animate-spin">✨</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 via-purple-900 to-slate-600">
              Selected Work Highlights
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              A snapshot of recent work that reflects our execution style across industries. Each project represents our commitment to quality and innovation.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm font-bold uppercase tracking-widest">
              <div className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-blue-100 to-blue-50 text-blue-700 border border-blue-200 hover:shadow-md transition-all group-hover:scale-105 flex items-center gap-2">
                <span className="text-lg">📊</span>
                <span>Projects DELIVERED</span>
              </div>
              <div className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-amber-100 to-amber-50 text-amber-700 border border-amber-200 hover:shadow-md transition-all group-hover:scale-105 flex items-center gap-2">
                <span className="text-lg">🔴</span>
                <span>Live & Active</span>
              </div>
              <div className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-100 to-green-50 text-green-700 border border-green-200 hover:shadow-md transition-all group-hover:scale-105 flex items-center gap-2">
                <span className="text-lg">⚙️</span>
                <span>Full Stack</span>
              </div>
            </div>
          </div>

          <div 
            className="flex flex-wrap gap-3 justify-center mb-12"
            style={{
              animation: projectsVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
              animationDelay: '0.1s',
              opacity: 1,
            }}
          >
            {allCategories.map((cat, idx) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-5 py-2.5 rounded-full font-bold text-sm uppercase tracking-widest transition-all duration-300 transform hover:scale-110 relative overflow-hidden group ${
                  activeFilter === cat
                    ? 'bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-lg shadow-slate-900/40 scale-105'
                    : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-amber-300 hover:shadow-md'
                }`}
                style={{
                  animation: projectsVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
                  animationDelay: projectsVisible ? `${0.15 + idx * 0.05}s` : '0s',
                  opacity: 1,
                }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <span className="group-hover:scale-125 transition-transform">
                    {cat === 'All' ? '🎯' : cat === 'Education' ? '🎓' : cat === 'Transport' ? '🚗' : cat === 'Professional Services' ? '💼' : cat === 'Restaurant' ? '🍽️' : '✨'}
                  </span>
                  {cat}
                </span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {filteredProjects.map((item, idx) => {
              const isExternal = item.link.startsWith('http');
              const colors = categoryColors[item.category] || categoryColors['Professional Services'];
              return (
                <article 
                  key={item.title} 
                  className={`${colors.bg} ${colors.border} border-2 rounded-3xl p-6 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 group relative overflow-hidden`}
                  style={{
                    animation: projectsVisible ? `fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards` : 'none',
                    animationDelay: projectsVisible ? `${idx * 0.08}s` : '0s',
                    opacity: 1,
                  }}
                >
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-300/30 to-transparent rounded-full blur-3xl -z-10 group-hover:scale-150 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/0 group-hover:from-white/30 group-hover:to-white/15 transition-all duration-300" />
                  
                  <div className="relative flex items-start justify-between mb-4">
                    <div className={`${colors.badge} px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest flex items-center gap-2 group-hover:scale-110 transition-transform shadow-sm`}>
                      <span className="text-sm">{colors.icon}</span>
                      <span>{item.category}</span>
                    </div>
                    <span className="text-2xl group-hover:translate-x-2 group-hover:scale-125 transition-all duration-300">→</span>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-2.5 group-hover:text-amber-600 transition-colors duration-300 line-clamp-2 relative">
                    {item.title}
                  </h3>

                  {item.stats && (
                    <div className="flex gap-3 mb-4 text-xs font-bold uppercase tracking-widest">
                      <span className="px-3 py-1 rounded-lg bg-white/60 text-slate-700 group-hover:text-slate-900 transition-colors border border-slate-200/60 shadow-sm">{item.stats.year}</span>
                      <span className="px-3 py-1 rounded-lg bg-white/60 text-slate-700 group-hover:text-slate-900 transition-colors border border-slate-200/60 shadow-sm">{item.stats.type}</span>
                    </div>
                  )}
                  
                  <p className="text-slate-600 leading-relaxed mb-5 text-sm group-hover:text-slate-800 transition-colors flex-grow relative font-medium">
                    {item.desc}
                  </p>

                  <div className="flex items-center gap-3 pt-4 border-t-2 border-slate-200 group-hover:border-amber-400 transition-colors relative">
                    <a
                      href={item.link}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 hover:text-amber-600 transition-colors flex-1 group/link"
                    >
                      <span>View Project</span>
                      <svg className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </a>
                    {isExternal && (
                      <svg className="w-4 h-4 text-slate-400 group-hover:text-amber-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg text-slate-600">No projects found in this category.</p>
            </div>
          )}

          <div 
            className="text-center"
            style={{
              animation: projectsVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
              animationDelay: '0.2s',
              opacity: 1,
            }}
          >
            <button
              onClick={() => {
                const scrollToPortfolio = () => {
                  const element = document.getElementById('portfolio-section');
                  if (element) {
                    window.scrollTo({ top: element.offsetTop - 100, behavior: 'smooth' });
                  }
                };
                scrollToPortfolio();
                setTimeout(() => scrollToPortfolio(), 300);
              }}
              className="inline-flex items-center justify-center px-8 py-4 border border-slate-900 bg-slate-900 text-white font-bold uppercase tracking-widest text-xs sm:text-sm rounded-xl hover:bg-amber-500 hover:text-black hover:border-amber-500 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_rgba(245,158,11,0.5)] group"
            >
              Explore All Projects
              <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <section ref={leadershipRef} className="py-16 lg:py-24 bg-gradient-to-b from-white via-slate-50 to-white border-b border-slate-200 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 left-1/4 w-96 h-96 bg-cyan-200/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-40 right-1/4 w-96 h-96 bg-emerald-200/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/2 left-0 w-96 h-96 bg-purple-200/10 rounded-full blur-3xl animate-pulse" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div 
            className="text-center mb-16"
            style={{
              animation: leadershipVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
              opacity: leadershipVisible ? 1 : 0,
            }}
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-50 to-emerald-50 border border-cyan-200 mb-6 shadow-sm">
              <span className="text-xl">👥</span>
              <span className="text-xs font-bold text-cyan-700 uppercase tracking-widest">Leadership Team</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 via-cyan-900 to-slate-600">
              Meet the Builders
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Our Directors lead business and technology with direct ownership of delivery outcomes, quality, and client success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {leadership.map((member, idx) => (
              <div 
                key={member.name} 
                className="bg-white/60 backdrop-blur-md rounded-3xl border-2 border-slate-200/60 p-6 sm:p-8 shadow-sm hover:shadow-2xl hover:border-cyan-300 hover:bg-white transition-all duration-500 hover:-translate-y-4 group relative overflow-hidden"
                style={{
                  animation: leadershipVisible ? 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
                  animationDelay: leadershipVisible ? `${idx * 0.15}s` : '0s',
                  opacity: leadershipVisible ? 1 : 0,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 via-transparent to-emerald-500/0 group-hover:from-cyan-500/5 group-hover:to-emerald-500/5 transition-all duration-300" />
                <div className="absolute -top-20 -right-20 w-48 h-48 bg-cyan-300/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative grid sm:grid-cols-[180px_1fr] gap-6 items-start">
                  <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border-2 border-slate-300 group-hover:border-cyan-400 transition-all duration-300 shadow-lg hover:shadow-2xl">
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent group-hover:from-slate-900/60 transition-all duration-300" />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-cyan-400/20 to-transparent transition-opacity duration-300" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-1.5 group-hover:text-cyan-600 transition-colors">{member.name}</h3>
                    <p className="text-xs font-bold text-cyan-600 uppercase tracking-widest mb-4 group-hover:text-cyan-700 transition-colors flex items-center gap-2">
                      <span>⭐</span>
                      <span>{member.role}</span>
                    </p>
                    <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors font-medium">{member.bio}</p>
                    <div className="mt-6 pt-5 border-t-2 border-slate-200 group-hover:border-cyan-300 transition-colors">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">🔗 Connect</p>
                      <div className="flex gap-3">
                        <a 
                          href={member.social?.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-slate-200 flex items-center justify-center text-slate-600 hover:from-blue-100 hover:to-blue-50 hover:border-blue-500 hover:text-blue-700 transition-all duration-300 hover:scale-125 hover:shadow-lg group-hover:scale-110"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                          </svg>
                        </a>
                        <a 
                          href={member.social?.twitter} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-slate-200 flex items-center justify-center text-slate-600 hover:from-sky-100 hover:to-sky-50 hover:border-sky-500 hover:text-sky-700 transition-all duration-300 hover:scale-125 hover:shadow-lg group-hover:scale-110"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2s9 5 20 5a9.5 9.5 0 00-9-5.5c4.75 2.25 7-7 7-7" />
                          </svg>
                        </a>
                        <a 
                          href={member.social?.email} 
                          className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-slate-200 flex items-center justify-center text-slate-600 hover:from-amber-100 hover:to-orange-50 hover:border-amber-500 hover:text-amber-700 transition-all duration-300 hover:scale-125 hover:shadow-lg group-hover:scale-110"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMjU1LCAyNTUsIDI1NSwgMC4wNSkiLz48L3N2Zz4=')] opacity-40" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-transparent rounded-full blur-[120px] pointer-events-none animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-amber-600/10 to-transparent rounded-full blur-3xl" />

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white mb-6 tracking-tight animate-fadeInUp leading-tight">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-orange-300 to-red-300">Ready to Build</span> Your Next Project?
          </h2>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            Talk with our team to define scope, architecture priorities, and a practical execution roadmap. We're here to turn your vision into reality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 text-sm font-bold tracking-wide rounded-xl hover:from-amber-400 hover:to-orange-400 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-500/40 group border border-amber-400/50"
            >
              <span>🚀 Start Building</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <a
              href="/#get-in-touch"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-slate-500 text-white text-sm font-bold tracking-wide rounded-xl hover:border-amber-400 hover:text-amber-300 hover:bg-slate-800/50 transition-all hover:-translate-y-1 group backdrop-blur-sm"
            >
              <span>📞 Contact Sales</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
}
